// Synthesized sound effects via Web Audio API (no external files / no copyright).

let ctx = null;
let muted = false;
let unlockersInstalled = false;

function createCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  const c = new AC();
  // Auto-recover whenever the browser suspends/interrupts the context.
  c.onstatechange = () => { if (c.state !== "running" && c.state !== "closed") c.resume().catch(() => {}); };
  return c;
}

function resumeCtx() {
  if (typeof window === "undefined") return;
  if (ctx && ctx.state === "closed") ctx = createCtx();      // stale after BFCache/close
  if (ctx && ctx.state !== "running") ctx.resume().catch(() => {});
}

// Keep audio alive across tab switches, focus loss and back/forward cache restores.
function installUnlockers() {
  if (unlockersInstalled || typeof window === "undefined") return;
  unlockersInstalled = true;
  const kick = () => resumeCtx();
  ["pointerdown", "touchstart", "keydown", "click"].forEach((e) =>
    window.addEventListener(e, kick, { passive: true })
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") resumeCtx();
  });
  window.addEventListener("focus", kick);
  window.addEventListener("pageshow", kick);
}

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx || ctx.state === "closed") {
    ctx = createCtx();
    if (!ctx) return null;
  }
  installUnlockers();
  if (ctx.state !== "running") ctx.resume().catch(() => {});
  return ctx;
}

// Play a 1-sample silent buffer — the canonical iOS trick to force-unlock audio inside a gesture.
function unlock(c) {
  try {
    const b = c.createBuffer(1, 1, 22050);
    const src = c.createBufferSource();
    src.buffer = b;
    src.connect(c.destination);
    src.start(0);
  } catch (e) {}
}

// Call on a user gesture (e.g. spin/Open click) to unlock audio and recover from interruptions.
export function primeAudio() {
  let c = getCtx();
  if (!c) return;
  // iOS/Safari: after app-switch the context sits in "interrupted"/"suspended".
  // We're inside a user gesture here, so resume + silent-buffer unlock reliably recovers it.
  if (c.state !== "running") c.resume().catch(() => {});
  if (c.state === "closed") { ctx = createCtx(); c = ctx; if (c) c.resume().catch(() => {}); }
  if (c) unlock(c);
}

export function setMuted(v) { muted = v; }
export function isMuted() { return muted; }

// Attach resume listeners immediately so audio recovers on any return visit.
if (typeof window !== "undefined") installUnlockers();

function tone(c, freq, start, dur, type = "sine", peak = 0.2) {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g).connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.05);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch (e) {} };
}

function glide(c, f1, f2, start, dur, type = "sawtooth", peak = 0.2) {
  const osc = c.createOscillator();
  const g = c.createGain();
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(1400, start);
  osc.type = type;
  osc.frequency.setValueAtTime(f1, start);
  osc.frequency.exponentialRampToValueAtTime(Math.max(40, f2), start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + 0.03);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(lp).connect(g).connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.05);
  osc.onended = () => { try { osc.disconnect(); lp.disconnect(); g.disconnect(); } catch (e) {} };
}

// Punchy kick drum — sine pitch-drop with a fast attack.
function kick(c, start, peak = 0.24) {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(190, start);
  osc.frequency.exponentialRampToValueAtTime(46, start + 0.12);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, start + 0.17);
  osc.connect(g).connect(c.destination);
  osc.start(start);
  osc.stop(start + 0.2);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch (e) {} };
}

// Crisp hi-hat — short filtered noise burst.
function hat(c, start, peak = 0.06) {
  const dur = 0.045;
  const buf = c.createBuffer(1, Math.max(1, Math.floor(c.sampleRate * dur)), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 7200;
  const g = c.createGain();
  g.gain.setValueAtTime(peak, start);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(hp).connect(g).connect(c.destination);
  src.start(start);
  src.stop(start + dur);
  src.onended = () => { try { src.disconnect(); hp.disconnect(); g.disconnect(); } catch (e) {} };
}

// Two detuned saws = harsh "beating" buzz (annoying on purpose)
function buzz(c, fa, fb, start, dur, peak = 0.16) {
  [fa, fb].forEach((f) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(f, start);
    // rapid gate for an alarm-like stutter
    g.gain.setValueAtTime(0.0001, start);
    for (let t = 0; t < dur; t += 0.09) {
      g.gain.setValueAtTime(peak, start + t);
      g.gain.setValueAtTime(0.02, start + t + 0.045);
    }
    g.gain.setValueAtTime(0.0001, start + dur);
    osc.connect(g).connect(c.destination);
    osc.start(start);
    osc.stop(start + dur + 0.05);
    osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch (e) {} };
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Selectable reel-tick sounds (chosen in Profile → Settings).
// The solo case reel uses playTick() which dispatches to the chosen sound.
// Battle reels use playTickSimple() — always the crisp classic blip so 4
// reels at once don't get overwhelming.
// ─────────────────────────────────────────────────────────────────────────

export const REEL_SOUNDS = [
  { key: "classic", label: "Classic Blip", desc: "The original crisp case tick." },
  { key: "beat", label: "Drum Beat", desc: "Punchy kick-and-hat groove." },
  { key: "marimba", label: "Happy Marimba", desc: "Bouncy, cheerful plucks." },
  { key: "truck", label: "Ice-Cream Truck", desc: "\u201cTurkey in the Straw\u201d jingle." },
  { key: "cucaracha", label: "La Cucaracha", desc: "Old arcade slot-machine chiptune." },
  { key: "off", label: "Off", desc: "No reel sound." },
];

let reelSound = "classic";
try { const s = typeof localStorage !== "undefined" && localStorage.getItem("uc_reel_sound"); if (s) reelSound = s; } catch (e) {}
export function getReelSound() { return reelSound; }
export function setReelSound(v) { reelSound = v; try { localStorage.setItem("uc_reel_sound", v); } catch (e) {} }

const NOTE = {
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0,
};
const TRUCK_TUNE = ["G4","G4","F4","E4","D4","E4","F4","G4","C4","E4","G4","C5","A4","F4","G4","G4","G4","G4","F4","E4","D4","E4","F4","G4","C4","G4","E4","C4","C4","G4","C4","C4"];
const CUCA_TUNE = ["C4","C4","C4","F4","A4","C4","C4","C4","F4","A4","F4","F4","E4","E4","D4","D4","C4","C4","C4","C4","E4","G4","C4","C4","C4","E4","G4","G4","G4","F4","F4","E4","E4","D4"];
const MARIMBA_SCALE = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5, 880.0, 783.99];

// Classic: crisp static square tick (no pitch sweep — the clean case tick).
function tickClassic(c, now, peak = 0.16) {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(1250, now);
  g.gain.setValueAtTime(peak, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + 0.05);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch (e) {} };
}

function tickBeat(c, now, n) {
  const step = n % 4;
  if (step === 0 || step === 2) kick(c, now, 0.22);
  else hat(c, now, step === 3 ? 0.075 : 0.055);
}

function tickMarimba(c, now, n) {
  const f = MARIMBA_SCALE[n % MARIMBA_SCALE.length];
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(f, now);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.14, now + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  osc.connect(g).connect(c.destination);
  osc.start(now); osc.stop(now + 0.2);
  osc.onended = () => { try { osc.disconnect(); g.disconnect(); } catch (e) {} };
  const o2 = c.createOscillator();
  const g2 = c.createGain();
  o2.type = "sine";
  o2.frequency.setValueAtTime(f * 2, now);
  g2.gain.setValueAtTime(0.0001, now);
  g2.gain.exponentialRampToValueAtTime(0.03, now + 0.006);
  g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
  o2.connect(g2).connect(c.destination);
  o2.start(now); o2.stop(now + 0.12);
  o2.onended = () => { try { o2.disconnect(); g2.disconnect(); } catch (e) {} };
}

function tickChip(c, now, f, cutoff) {
  const osc = c.createOscillator();
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(cutoff, now);
  const g = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(f, now);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.11, now + 0.006);
  g.gain.exponentialRampToValueAtTime(0.02, now + 0.11);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.19);
  osc.connect(lp).connect(g).connect(c.destination);
  osc.start(now); osc.stop(now + 0.22);
  osc.onended = () => { try { osc.disconnect(); lp.disconnect(); g.disconnect(); } catch (e) {} };
}

function tickTruck(c, now, n) {
  const f = NOTE[TRUCK_TUNE[n % TRUCK_TUNE.length]];
  tickChip(c, now, f, 3800);
  const o2 = c.createOscillator();
  const g2 = c.createGain();
  o2.type = "triangle";
  o2.frequency.setValueAtTime(f * 2, now);
  g2.gain.setValueAtTime(0.0001, now);
  g2.gain.exponentialRampToValueAtTime(0.035, now + 0.006);
  g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
  o2.connect(g2).connect(c.destination);
  o2.start(now); o2.stop(now + 0.15);
  o2.onended = () => { try { o2.disconnect(); g2.disconnect(); } catch (e) {} };
}

function tickCucaracha(c, now, n) {
  const f = NOTE[CUCA_TUNE[n % CUCA_TUNE.length]];
  tickChip(c, now, f, 4600);
  const o2 = c.createOscillator();
  const g2 = c.createGain();
  o2.type = "square";
  o2.frequency.setValueAtTime(f * 1.007, now);
  g2.gain.setValueAtTime(0.0001, now);
  g2.gain.exponentialRampToValueAtTime(0.05, now + 0.005);
  g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
  o2.connect(g2).connect(c.destination);
  o2.start(now); o2.stop(now + 0.15);
  o2.onended = () => { try { o2.disconnect(); g2.disconnect(); } catch (e) {} };
}

function tickWith(key, c, now, n) {
  switch (key) {
    case "off": return;
    case "beat": return tickBeat(c, now, n);
    case "marimba": return tickMarimba(c, now, n);
    case "truck": return tickTruck(c, now, n);
    case "cucaracha": return tickCucaracha(c, now, n);
    default: return tickClassic(c, now);
  }
}

// Battle reels — always the crisp classic blip, a touch quieter.
export function playTickSimple() {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  tickClassic(c, c.currentTime, 0.09);
}

// Solo case reel — plays the user's chosen sound. Melodic sounds keep a
// running note index that auto-resets between spins (gap > 0.5s).
let tickN = 0, lastTickAt = 0;
export function playTick() {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  if (now - lastTickAt > 0.5) tickN = 0;
  lastTickAt = now;
  tickWith(reelSound, c, now, tickN);
  tickN++;
}

// Play a short preview of a given reel sound (used by the settings picker).
export function playReelSample(key) {
  const c = getCtx();
  if (!c) return;
  if (key === "off") return;
  let n = 0;
  const step = () => {
    tickWith(key, c, c.currentTime, n);
    n++;
    if (n < 9) setTimeout(step, 115);
  };
  step();
}

// WIN (happy ending): bright ascending major arpeggio resolving into a
// full, joyful chord — punctuated with a kick so it lands on the beat.
// intensity ~0.4 (common) .. 3.2 (mythic) widens the final chord/sparkle.
export function playRareWin(intensity = 1) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  kick(c, now, 0.2); // drop hit to land the beat
  const arp = [523.25, 659.25, 783.99, 1046.5]; // C E G C — happy major
  arp.forEach((f, i) => {
    const t = now + i * 0.1;
    tone(c, f, t, 0.45, "triangle", 0.26);
    tone(c, f * 2, t, 0.28, "sine", 0.05); // shimmer octave
  });
  const chordAt = now + arp.length * 0.1;
  const chord = intensity >= 1.5
    ? [523.25, 659.25, 783.99, 1046.5, 1318.51]
    : [523.25, 659.25, 783.99, 1046.5];
  chord.forEach((f) => tone(c, f, chordAt, 1.3 + Math.min(intensity, 3) * 0.15, "sine", 0.12));
  kick(c, chordAt, 0.18); // second kick under the resolve
  // Joyful top sparkle to finish.
  [1567.98, 2093.0].forEach((f, i) => tone(c, f, chordAt + 0.08 + i * 0.07, 0.5, "triangle", 0.05));
}

// WIN: bright, chill, dopamine ascending arpeggio + soft pad
export function playWin() {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const arp = [523.25, 659.25, 783.99, 1046.5, 1318.51]; // C5 E5 G5 C6 E6
  arp.forEach((f, i) => {
    const t = now + i * 0.11;
    tone(c, f, t, 0.55, "sine", 0.22);
    tone(c, f * 2, t, 0.4, "triangle", 0.05); // shimmer
  });
  // warm sustained chord for the chill vibe
  [523.25, 659.25, 783.99].forEach((f) => tone(c, f, now + 0.55, 1.4, "sine", 0.08));
}

// LOSE: harsh, dissonant, rage-inducing buzzer + descending fail
export function playLose() {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  buzz(c, 233.08, 246.94, now, 0.34);       // dissonant beating alarm
  buzz(c, 185.0, 196.0, now + 0.34, 0.32);
  glide(c, 320, 90, now + 0.05, 0.75, "sawtooth", 0.16); // descending "wah" fail
}
